/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import can from 'can';
import urlUtil from 'scripts/utils/url';
import VideoFilter from 'components/video-detail/video-feed/video-filter';
import FeedDataService from 'scripts/services/timeline-feed-service';
import dtmService from 'scripts/services/dtm';
import DropdownSearch from 'components/dropdown/dropdown-container/dropdown-search';
import template from './video-feed.stache';

Component.extend({
    tag: 'video-feed',
    template: template,
    leakScope: false,
    viewModel: {
        sponsorIsAvailable: false,
        define: {
            type: {
                type: 'string',
                value: null,
            },
            sponsor: {
                Type: can.Map,
                Value: null,
            },
            filter1: {
                Type: VideoFilter,
                Value: null,
            },
            filter2: {
                Type: VideoFilter,
                Value: null,
            },
            activeCard: {
                Type: can.Map,
                Value: null,
            },
            cards: {
                Type: can.List,
                Value: can.List,
            },
            fetching: {
                type: 'boolean',
                value: false,
            },
            loadMore: {
                type: 'boolean',
                value: false,
            },
            currentPage: {
                type: 'number',
                value: 1,
            },
            dtmTrack: {
                type: 'boolean',
            },
            dtmVideoLocation: {
                type: 'string',
            },
        },
        reloadCards() {
            this.loadCards(1).then(() => this.loadCards(2));
        },
        loadCards(page) {
            if (this.attr('fetching')) {
                return;
            }
            if (page === 1) {
                this.attr('cards').replace([]);
            }
            this.attr('fetching', true);
            return this.timelineLoader
                .loadVideoDetail(
                    this.attr('type'),
                    this.attr('filter1.value.key') ||
                        this.attr('filter1.value'),
                    this.attr('filter2.value.key') ||
                        this.attr('filter2.value'),
                    page,
                )
                .then(([newCards, total, index]) => {
                    can.batch.start();
                    const cards = this.attr('cards');
                    cards.push.apply(cards, newCards);
                    this.attr('fetching', false);
                    this.attr('loadMore', total > index);
                    this.attr('currentPage', index);
                    can.batch.stop();
                })
                .fail(() => {
                    this.attr('fetching', false);
                });
        },
        selectCard(event) {
            if (event && event.payload.card) {
                this.attr('dtmTrack', true);
                this.attr('dtmVideoLocation', 'Video Detail Feed');
                this.attr('activeCard', event.payload.card);
            }
        },
        isActiveCard(card) {
            const activeCard = this.attr('activeCard');
            return activeCard
                ? activeCard.identifier === card.identifier
                : false;
        },
        updateSponsor() {
            const sponsor =
                this.attr('filter1.value.sponsor') ||
                this.attr('filter2.value.sponsor');
            if (
                this.attr('type') === 'featured' &&
                sponsor !== this.attr('sponsor')
            ) {
                this.attr('sponsor', sponsor);
            }
            this.attr('sponsorIsAvailable', !!this.attr('sponsor.image'));
        },
        loadFilters() {
            this.timelineLoader.loadVideoFilters().then((filters) => {
                const type = this.attr('type');
                can.batch.start();
                if (type === 'highlights') {
                    this.attr(
                        'filter1',
                        this.createFilter(filters, 'round', []),
                    );
                    this.attr(
                        'filter2',
                        this.createFilter(filters, 'player', []),
                    );
                    dtmService.trackVideoFilter(
                        this.attr('filter2').value.title,
                    );
                }
                if (type === 'featured') {
                    this.attr(
                        'filter1',
                        this.createFilter(
                            filters,
                            'category',
                            this.attr('activeCard.tags') || [],
                        ),
                    );
                    this.attr(
                        'filter2',
                        this.createFilter(filters, 'date', []),
                    );
                    this.updateDateFilter();
                    dtmService.trackVideoFilter(
                        this.attr('filter1.value.title'),
                    );
                }
                this.reloadCards();
                this.updateSponsor();
                can.batch.stop();
            });
        },
        updateDateFilter() {
            this.timelineLoader
                .loadVideoFilters(this.attr('filter1.value.key'))
                .then((filters) => {
                    this.attr(
                        'filter2',
                        this.createFilter(filters, 'date', []),
                    );
                });
        },
        createFilter(filters, name, activeTags) {
            const filter = filters[name];
            if (!filter) {
                return null;
            }
            let videoFilter = new VideoFilter({
                items: filter,
                tags: activeTags,
            });
            let category = urlUtil.getHash().slice(2);
            if (name === 'category' && category) {
                let categoryItem = null;
                videoFilter.attr('items').each((item) => {
                    if (item.key === category) {
                        categoryItem = item;
                        return false;
                    }
                });
                if (categoryItem !== null) {
                    videoFilter.attr('value', categoryItem);
                }
            }
            if (name === 'player') {
                videoFilter.attr(
                    'search',
                    new DropdownSearch({
                        filterType: name,
                        filter: function(item, searchValue) {
                            if (!searchValue) {
                                return true;
                            }
                            return item.title
                                .toLowerCase()
                                .includes(searchValue.toLowerCase());
                        },
                    }),
                );
            }
            return videoFilter;
        },
    },
    init() {
        this.viewModel.timelineLoader = new FeedDataService();
        this.viewModel.bind('filter1.value', (ev, value) => {
            if (this.viewModel.attr('type') === 'featured') {
                dtmService.trackVideoFilter(value.title);
                this.viewModel.updateDateFilter();
            }
            this.viewModel.reloadCards();
            this.viewModel.updateSponsor();
        });
        this.viewModel.bind('filter2.value', (ev, value) => {
            if (this.viewModel.attr('type') === 'highlights') {
                dtmService.trackVideoFilter(value.title);
            }
            this.viewModel.reloadCards();
            this.viewModel.updateSponsor();
        });
        this.viewModel.loadFilters();
    },

    events: {
        '.s-load-more click'() {
            this.viewModel.loadCards(this.viewModel.attr('currentPage') + 1);
        },
    },
});
